import { useEffect, useRef, useState } from 'react';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSavedItems } from '@/hooks/useSavedItems';

/**
 * Splash Screen Hook
 *
 * Smart logic to prevent flash of empty content:
 * 1. Wait for auth to resolve
 * 2. If authenticated, wait for initial content load
 * 3. Hide splash with fade animation
 *
 * Safety guards:
 * - Don't wait if user is not authenticated
 * - Max 3 second timeout (covers new users and slow networks)
 * - Only hides when content actually exists or timeout reached
 */
export const useSplashScreen = () => {
  const { user, loading: authLoading } = useAuthContext();
  const { items, isLoading: itemsLoading } = useSavedItems();
  const hasHiddenRef = useRef(false);
  const [timeoutReached, setTimeoutReached] = useState(false);

  // Ensure splash is visible on mount (transition from native LaunchScreen to plugin splash)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    console.log('[SplashScreen] Ensuring splash is visible on mount...');
    SplashScreen.show({
      autoHide: false,
      fadeInDuration: 0, // Instant, no fade in since we're transitioning from native splash
    }).catch((error) => {
      console.error('[SplashScreen] Error showing splash:', error);
    });
  }, []);

  // Safety timeout - never wait more than 3 seconds for content
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('[SplashScreen] Timeout reached (3s)');
      setTimeoutReached(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Already hidden
    if (hasHiddenRef.current) {
      return;
    }

    // Still loading auth
    if (authLoading) {
      console.log('[SplashScreen] Waiting for auth to complete...');
      return;
    }

    // Determine if we should hide splash
    // Strategy: Wait for actual content OR timeout (simpler, more reliable)
    const hasContent = items.length > 0;

    const shouldHide =
      !user ||              // Not authenticated - nothing to load
      hasContent ||         // User has saves loaded (actual data present)
      timeoutReached;       // Timeout - includes new users and slow networks

    console.log('[SplashScreen] Evaluating hide conditions:', {
      authenticated: !!user,
      hasContent,
      itemsLoading,
      itemsCount: items.length,
      timeoutReached,
      shouldHide,
    });

    if (shouldHide) {
      hasHiddenRef.current = true;

      console.log('[SplashScreen] ✅ Hiding splash (reason:',
        !user ? 'not authenticated' :
        hasContent ? `has ${items.length} items` :
        'timeout reached',
      ')');

      // Small delay when content exists to ensure React renders before fade starts
      // This prevents flash of empty state during splash fade
      const delay = hasContent ? 150 : 0;

      setTimeout(() => {
        console.log('[SplashScreen] Starting fade out...');
        SplashScreen.hide({
          fadeOutDuration: 300,
        }).catch((error) => {
          console.error('[SplashScreen] Error hiding splash:', error);
        });
      }, delay);
    } else {
      console.log('[SplashScreen] ⏳ Waiting for content...');
    }
  }, [authLoading, user, items, itemsLoading, timeoutReached]);
};
